import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  UsersIcon, 
  BuildingOfficeIcon,
  ServerIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const OverviewDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOverviewData();
    const interval = setInterval(fetchOverviewData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const fetchOverviewData = async () => {
    try {
      const [metricsResponse, healthResponse] = await Promise.all([
        axios.get('/api/platform/overview/metrics'),
        axios.get('/api/platform/health')
      ]);
      setMetrics(metricsResponse.data);
      setHealth(healthResponse.data);
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
        <span className="ml-3 text-gray-600">Loading platform overview...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BuildingOfficeIcon className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Tenants</dt>
                  <dd className="text-lg font-medium text-gray-900">{metrics?.totalTenants || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UsersIcon className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Users</dt>
                  <dd className="text-lg font-medium text-gray-900">{metrics?.activeUsers || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-8 w-8 text-purple-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">API Calls (24h)</dt>
                  <dd className="text-lg font-medium text-gray-900">{metrics?.apiCalls24h?.toLocaleString() || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ServerIcon className="h-8 w-8 text-orange-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">System Health</dt>
                  <dd className="flex items-center">
                    {health?.overall === 'healthy' ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-1" />
                    ) : (
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-1" />
                    )}
                    <span className="text-lg font-medium text-gray-900">
                      {health?.overall === 'healthy' ? 'Healthy' : 'Issues'}
                    </span>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Activity */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Platform Activity</h3>
        </div>
        <div className="p-6">
          {metrics ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{metrics.totalUsers}</div>
                <div className="text-sm text-gray-500">Total Users</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{metrics.totalItems}</div>
                <div className="text-sm text-gray-500">Inventory Items</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{metrics.activeReservations}</div>
                <div className="text-sm text-gray-500">Active Reservations</div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Loading Activity Data...</h3>
            </div>
          )}
        </div>
      </div>

      {/* System Health Summary */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">System Health Summary</h3>
        </div>
        <div className="p-6">
          {health ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">Overall Status</span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  health.status === 'healthy' ? 'bg-green-100 text-green-800' :
                  health.status === 'degraded' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {health.status === 'healthy' && <CheckCircleIcon className="h-4 w-4 mr-1" />}
                  {health.status === 'degraded' && <ExclamationTriangleIcon className="h-4 w-4 mr-1" />}
                  {health.status === 'unhealthy' && <ExclamationTriangleIcon className="h-4 w-4 mr-1" />}
                  {health.status.charAt(0).toUpperCase() + health.status.slice(1)}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Healthy Checks:</span>
                  <span className="ml-2 font-medium text-green-600">{health.summary?.healthy || 0}</span>
                </div>
                <div>
                  <span className="text-gray-500">Unhealthy Checks:</span>
                  <span className="ml-2 font-medium text-red-600">{health.summary?.unhealthy || 0}</span>
                </div>
              </div>

              {health.checks && (
                <div className="mt-4 space-y-2">
                  {Object.entries(health.checks).slice(0, 3).map(([key, check]) => (
                    <div key={key} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{check.name}</span>
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                        check.status === 'healthy' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {check.status}
                      </span>
                    </div>
                  ))}
                  {Object.keys(health.checks).length > 3 && (
                    <div className="text-xs text-gray-400 text-center mt-2">
                      +{Object.keys(health.checks).length - 3} more checks
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <ServerIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Loading Health Data...</h3>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OverviewDashboard;